<template>
    <rkm-form
        v-if="model"
        ref="RkmForm"
    >
        <rkm-row>
            <rkm-col>
                <rkm-form-select
                    :options="form.anos"
                    :validations="validations.ano"
                    @change="handleAnoChange"
                    label="Ano"
                    show-default-option
                    v-model="model.ano"
                />
            </rkm-col>

            <rkm-col>
                <rkm-form-select
                    :options="form.meses"
                    :validations="validations.mes"
                    @change="handleMesChange"
                    label="Mês"
                    show-default-option
                    text-field="valor"
                    v-model="model.competenciaId"
                    value-field="id"
                />
            </rkm-col>
        </rkm-row>

        <rkm-row>
            <rkm-col>
                <rkm-form-select
                    :options="form.cnes"
                    :validations="validations.cnes"
                    @change="handleCnesChange"
                    default-option-text="Todos"
                    label="CNES"
                    show-default-option
                    v-model="model.cnes"
                />
            </rkm-col>

            <rkm-col>
                <rkm-form-select
                    :options="form.unidades"
                    :validations="validations.unidadeId"
                    default-option-text="Todas"
                    label="Unidade"
                    show-default-option
                    text-field="nomeFantasia"
                    v-model="model.unidadeId"
                    value-field="id"
                />
            </rkm-col>
        </rkm-row>

        <rkm-row v-if="form.financiamentos.length > 0">
            <rkm-col>
                <rkm-form-group
                    label-for="Financiamento"
                    label="Financiamento"
                >
                    <rkm-form-checkbox
                        :indeterminate="indeterminate"
                        @change="handleFinanciamentosChange"
                        v-model="selectedAll"
                    >
                        Todos
                    </rkm-form-checkbox>

                    <rkm-form-checkbox-group
                        :options="form.financiamentos"
                        :validations="validations.financiamentos"
                        name="Financiamento"
                        stacked
                        text-field="nome"
                        v-model="model.financiamentos"
                        value-field="id"
                    />
                </rkm-form-group>
            </rkm-col>
        </rkm-row>
    </rkm-form>
</template>

<script>
// Enums.
import mesesAno from '@/enums/geral/mesesAno'

// Mixins.
import { error, repository, stringCase } from '@/mixins'

// Vuex.
import { mapActions } from 'vuex'

export default {
    computed: {
        validations() {
            return {
                ano: { required: true },
                mes: { required: true },
                financiamentos: { required: true },
            }
        },
        indeterminate() {
            return this.model.financiamentos.length > 0 && this.form.financiamentos.length !== this.model.financiamentos.length
        },
    },
    created() {
        this.repositories = {
            exportacao: this.getConectaSusRepository('bpa.exportacao'),
            financiamento: this.getConectaSusRepository('sigtap.financiamento'),
        }
    },
    data: () => ({
        enums: {mesesAno},
        form: null,
        model: null,
        selectedAll: false,
    }),
    methods: {
        ...mapActions([
            'setExportacaoBpaEmAndamento',
            'setLoading',
            'unsetLoading',
        ]),
        load() {
            this.setLoading()

            this.repositories.exportacao.create()
                .then(response => {
                    this.form = Object.assign(response.data.form)
                    this.model = Object.assign(response.data.model)
                })
                .catch(this.handleError)
                .finally(() => this.unsetLoading())
        },
        handleAnoChange(value) {
            this.form.meses = []
            this.form.cnes = []
            this.form.unidades = []
            this.form.financiamentos = []
            this.model.competenciaId = null
            this.model.cnes = null
            this.model.unidadeId = null
            this.model.financiamentos = []

            if (value === null) {
                return
            }

            const request = {
                listar: 'meses',
                ano: value,
            }

            this.setLoading()

            this.repositories.exportacao.list(this.camelObjectKeysToSnakeRecursive(request))
                .then(response => {
                    this.form.meses = response.data.meses.map(competencia => {
                        return {
                            id: competencia.id,
                            valor: this.enums.mesesAno[parseInt(competencia.valor)],
                        }
                    })
                })
                .catch(this.handleError)
                .finally(() => this.unsetLoading())
        },
        handleMesChange(value) {
            this.form.cnes = []
            this.form.unidades = []
            this.form.financiamentos = []
            this.model.cnes = null
            this.model.unidadeId = null
            this.model.financiamentos = []

            if (value === null) {
                return
            }

            const request = {
                listar: 'cnes',
                competenciaId: value,
            }

            this.setLoading()

            this.repositories.exportacao.list(this.camelObjectKeysToSnakeRecursive(request))
                .then(response => {
                    this.form.cnes = Object.assign(response.data.cnes)
                    this.form.financiamentos = Object.assign(response.data.financiamentos)
                })
                .catch(this.handleError)
                .finally(() => this.unsetLoading())
        },
        handleCnesChange(value) {
            this.form.unidades = []
            this.model.unidadeId = null

            if (value === null) {
                return
            }

            const request = {
                listar: 'unidades',
                competenciaId: this.model.competenciaId,
                cnes: value,
            }

            this.setLoading()

            this.repositories.exportacao.list(this.camelObjectKeysToSnakeRecursive(request))
                .then(response => {
                    this.form.unidades = Object.assign(response.data.unidades)

                    if (this.form.unidades.length === 1) {
                        this.model.unidadeId = this.form.unidades[0].id
                    }
                })
                .catch(this.handleError)
                .finally(() => this.unsetLoading())
        },
        handleFinanciamentosChange(selected) {
            this.model.financiamentos = selected ? this.form.financiamentos.map(financiamento => financiamento.id) : []
        },
        save() {
            this.$refs.RkmForm.validate()
                .then(valid => {
                    if (valid) {
                        const request = {
                            competenciaId: this.model.competenciaId,
                            cnes: this.model.cnes,
                            unidadeId: this.model.unidadeId,
                            financiamentos: this.model.financiamentos,
                        }

                        this.setLoading()

                        this.repositories.exportacao.store(this.camelObjectKeysToSnakeRecursive(request))
                            .then(() => {
                                this.showSaveNotification()
                                this.setExportacaoBpaEmAndamento(true)
                                this.$emit('saved')
                            })
                            .catch(this.handleError)
                            .finally(() => this.unsetLoading())
                    }
                })
        },
    },
    mixins: [ error, repository, stringCase ],
    mounted() {
        this.load()
    },
    watch: {
        'model.financiamentos'(newValue) {
            this.selectedAll = newValue.length > 0 && newValue.length === this.form.financiamentos.length
        },
    },
}
</script>